import React, { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { HotKeys, KeyMap } from 'react-hotkeys'
import { Route } from 'react-router-dom'
import {
  Badge
} from 'react-bootstrap'
import KanBoIcon from 'components/KanBoIcon'
import {
  NavbarTab,
  NavbarTabCounter,
} from 'components/Navbar'
import LinkContainer from 'components/LinkContainer'
import User from 'components/ControlledUser'
import * as UserActivityConfigTypes from 'const/UserActivityConfigTypes'
import {
  ModalMenu, navigationController
} from 'components/Modal'
import {
  MenuItem
} from 'components/Menu'
import UnreadNotifications from './Sidebar/UnreadNotifications'
import Bookmarks from './Sidebar/Bookmarks'
import UserSettings from './Sidebar/UserSettings'
import UserBlockers from './Sidebar/UserBlockers'
import UserProfile from './UserProfile'
import UserActivities from './UserActivities'
import classes from './Layout.module.scss'
import withLeftSidebar, { LeftSidebarProps } from 'components/Sidebar/withLeftSidebar'
import { useHistory } from 'react-router'
import { connect } from 'react-redux'
import {
  showProfile,
} from 'store/redux/layout'
import {
  setUserId as setUserActivitiesId
} from 'store/redux/userActivities'
import { createStructuredSelector } from 'reselect'
import { currentUserSelector } from 'selectors/currentUser'
import styled from 'styled-components'

const Navbar = styled.div`
  background-color: rgba(255, 255, 255, 0.8);
`

const keyMap: KeyMap = {
  'toggleNotifications': { sequence: 'q', action: 'keyup' },
  'toggleSearch': 's',
  'toggleMyBoard': 'm',
}

type OwnProps = {
  searchState?: any
  breadcrumb?: React.ReactNode
  viewMenu?: React.ReactNode
  children?: React.ReactNode
}

type StateProps = {
  config: any
  currentUser: any
  userBlockerCount: number
  unreadNotificationCount: number
  userActivityConfig: any
}

type DispatchProps = {
  showProfile: (...args: any[]) => void
  setUserActivitiesId: (...args: any[]) => void
}

type Props = React.HTMLProps<HTMLDivElement> & OwnProps & StateProps & DispatchProps & LeftSidebarProps

const Layout: React.FC<Props> = ({
  searchState,
  breadcrumb,
  viewMenu,
  children,

  config,
  currentUser,
  userBlockerCount,
  unreadNotificationCount,
  userActivityConfig,

  showProfile,
  setUserActivitiesId,

  leftSidebar,

  ...restProps
}) => {
  const intl = useIntl()

  const history = useHistory()

  const views = navigationController.useViews()

  const userCounter = useMemo(() =>
    userBlockerCount + unreadNotificationCount,
    [userBlockerCount, unreadNotificationCount]
  )

  const handleSearchKeyUp = (event) => {
    event.preventDefault()

    history.push('/search', searchState)
  }

  const handleMyBoardKeyUp = (event) => {
    event.preventDefault()

    history.push(`/myboard/${currentUser.id}`)
  }

  const handleUserBlockersClick = () => {
    leftSidebar.push(UserBlockers, { position: 'right' })
  }

  const handleNotificationsClick = () => {
    if (views.length > 0 && views[views.length - 1].id === 'notifications') {
      leftSidebar.pop()
    } else {
      leftSidebar.push(UnreadNotifications, { id: 'notifications', position: 'right' })
    }
  }

  const handleShowCurrentUserProfileClick = () => {
    showProfile({
      profileKey: currentUser.profileKey
    })
  }

  const handleShowUserActivitiesClick = () => {
    setUserActivitiesId(currentUser.id)
  }

  const handleBookmarksClick = () => {
    leftSidebar.push(Bookmarks, { position: 'right' })
  }

  const handleUserSettingsClick = () => {
    leftSidebar.push(UserSettings, { position: 'right' })
  }

  const handleToggleFullscreenClick = () => {
    const doc = document as any
    const elem = doc.documentElement as any
    if (!doc.fullscreenElement && !doc.mozFullScreenElement &&
      !doc.webkitFullscreenElement && !doc.msFullscreenElement) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen()
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen()
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen()
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen((Element as any).ALLOW_KEYBOARD_INPUT)
      }
    } else {
      if (doc.exitFullscreen) {
        doc.exitFullscreen()
      } else if (doc.msExitFullscreen) {
        doc.msExitFullscreen()
      } else if (doc.mozCancelFullScreen) {
        doc.mozCancelFullScreen()
      } else if (doc.webkitExitFullscreen) {
        doc.webkitExitFullscreen()
      }
    }
  }

  const handleHelpClick = () => {
    window.open(config.helpUrl ? config.helpUrl : intl.formatMessage({ id: 'HELP_URL' }), '_blank')
  }

  const keyHandlers = {
    'toggleNotifications': handleNotificationsClick,
    'toggleSearch': handleSearchKeyUp,
    'toggleMyBoard': handleMyBoardKeyUp,
  }

  const doc = document as any
  const elem = doc.documentElement as any

  const fullscreenEnabled =
    elem.requestFullscreen ||
    elem.msRequestFullscreen ||
    elem.mozRequestFullScreen ||
    elem.webkitRequestFullscreen

  return (
    <div
      id='layoutContent'
      {...restProps}
    >
      <HotKeys
        focused
        keyMap={keyMap}
        handlers={keyHandlers}
        attach={window}
      />
      <UserProfile />
      <UserActivities />
      <Navbar className={classes.navbar}>
        <div className={classes.navbarLeft}>
          {breadcrumb}
        </div>
        <div className={classes.navbarRight}>
          <Route
            path='/search'
          >
            {({ match }) =>
              <React.Fragment>
                <NavbarTab
                  active={Boolean(match)}
                  className={`${classes.personalTile} ${classes.searchTile}`}
                  onClick={() => {
                    if (match) {
                      history.goBack()
                    } else {
                      history.push('/search', searchState)
                    }
                  }}
                >
                  <KanBoIcon name='kanbo-24-search' />
                </NavbarTab>
                {match &&
                  <HotKeys
                    focused
                    keyMap={{ 'closeSearch': 'escape' }}
                    handlers={{ 'closeSearch': () => history.goBack() }}
                    attach={window}
                  />
                }
              </React.Fragment>
            }
          </Route>
          <div className={classes.tileWrapper}>
            {userBlockerCount > 0 &&
              <NavbarTab
                onClick={handleUserBlockersClick}
              >
                <KanBoIcon name='kanbo-24-block' />
                <NavbarTabCounter className='bg-red'>{userBlockerCount}</NavbarTabCounter>
              </NavbarTab>
            }
            <NavbarTab
              onClick={handleNotificationsClick}
            >
              <KanBoIcon name='kanbo-24-notifications' />
              <NavbarTabCounter className='bg-orange'>{unreadNotificationCount}</NavbarTabCounter>
            </NavbarTab>
            {(config.myBoard && currentUser.settings.tiles[1] === 'MyBoard') &&
              <Route
                path={`/myboard/${currentUser.id}`}
                exact
              >
                {({ match }) =>
                  <React.Fragment>
                    <LinkContainer
                      to={`/myboard/${currentUser.id}`}
                    >
                      <NavbarTab
                        active={Boolean(match)}
                        className={classes.personalTile}
                      >
                        <KanBoIcon name='kanbo-24-my-kanbo-board' />
                      </NavbarTab>
                    </LinkContainer>
                    {match &&
                      <HotKeys
                        focused
                        keyMap={{ 'closeSearch': 'escape' }}
                        handlers={{ 'closeSearch': () => history.goBack() }}
                        attach={window}
                      />
                    }
                  </React.Fragment>
                }
              </Route>
            }
            <NavbarTab
              onClick={handleHelpClick}
            >
              <KanBoIcon name='kanbo-24-help' />
            </NavbarTab>
          </div>
          <ModalMenu
            attachment='top right'
            targetAttachment='bottom right'
          >
            <div
              className={classes.userTile}
            >
              <User
                avatar
                id={currentUser.id}
                size={40}
              />

              {userCounter > 0 &&
                <NavbarTabCounter
                  className={`${classes.notificationCounter} ${userBlockerCount > 0 ? 'bg-red' : 'bg-orange'}`}
                >
                  {userCounter}
                </NavbarTabCounter>
              }
            </div>
            {userBlockerCount > 0 &&
              <MenuItem
                icon={<KanBoIcon fixedWidth name='kanbo-16-block' />}
                className={classes.flexMenuItem}
                onClick={handleUserBlockersClick}
              >
                <FormattedMessage id='BLOCKERS' />
                <Badge className={classes.marginLeftAuto}>{userBlockerCount}</Badge>
              </MenuItem>
            }
            <MenuItem
              icon={<KanBoIcon fixedWidth name='kanbo-16-notifications' />}
              className={classes.flexMenuItem}
              onClick={handleNotificationsClick}
            >
              <FormattedMessage id='NOTIFICATIONS_HEADER' />
              {unreadNotificationCount > 0 &&
                <Badge className={classes.marginLeftAuto}>{unreadNotificationCount}</Badge>
              }
            </MenuItem>
            <MenuItem
              icon={<KanBoIcon fixedWidth name='kanbo-16-bookmarked' />}
              onClick={handleBookmarksClick}
            >
              <FormattedMessage id='BOOKMARKS' />
            </MenuItem>
            <MenuItem
              icon={<KanBoIcon fixedWidth name='kanbo-16-settings' />}
              onClick={handleUserSettingsClick}
            >
              <FormattedMessage id='USER_SETTINGS' />
            </MenuItem>
            <MenuItem
              icon={<KanBoIcon fixedWidth name='kanbo-16-user' />}
              onClick={handleShowCurrentUserProfileClick}
            >
              <FormattedMessage id='USER_SHOW_PROFILE' />
            </MenuItem>
            {(
              userActivityConfig === UserActivityConfigTypes.ALL ||
              userActivityConfig === UserActivityConfigTypes.ONLY_ME
            ) &&
              <MenuItem
                icon={<KanBoIcon fixedWidth name='kanbo-16-stream' />}
                onClick={handleShowUserActivitiesClick}
              >
                <FormattedMessage id='ACTIVITY_STREAM_HEADER' />
              </MenuItem>
            }
            {config.myBoard &&
              <LinkContainer to={'/myboard/' + currentUser.id}>
                <MenuItem
                  icon={<KanBoIcon fixedWidth name='kanbo-16-my-board-2' />}
                >
                  <FormattedMessage id='MYBOARD' />
                </MenuItem>
              </LinkContainer>
            }
            {fullscreenEnabled &&
              <MenuItem
                icon={<KanBoIcon fixedWidth name='kanbo-16-full-screan' />}
                onClick={handleToggleFullscreenClick}
              >
                <FormattedMessage id='TOGGLE_FULLSCREEN' />
              </MenuItem>
            }
            <MenuItem
              icon={<KanBoIcon fixedWidth name='kanbo-16-help' />}
              onClick={handleHelpClick}
            >
              <FormattedMessage id='HELP' />
            </MenuItem>
          </ModalMenu>
        </div>
      </Navbar>
      {!!viewMenu &&
        <div className={classes.viewNavbar}>
          {viewMenu}
        </div>
      }
      {children}
    </div>
  )
}

const selector = createStructuredSelector<any, OwnProps, StateProps>({
  config: state => state.kanbo.config,
  currentUser: currentUserSelector,
  userBlockerCount: state => state.kanbo.userBlockers.count,
  unreadNotificationCount: state => state.kanbo.notifications.unreadCount,
  userActivityConfig: state => state.kanbo.config.userActivity,
})

export default withLeftSidebar(
  connect<StateProps, DispatchProps, OwnProps>(
    selector,
    {
      showProfile,
      setUserActivitiesId,
    },
  )(Layout)
)
